import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { type AppearanceMode, useAppearanceMode } from '@/hooks/useAppearanceMode';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-react';

const APPEARANCE_OPTIONS = [
  {
    value: 'light' as AppearanceMode,
    tooltipKey: 'sidenav.appearance.light' as TranslationKey,
    icon: <SunIcon />,
  },
  {
    value: 'dark' as AppearanceMode,
    tooltipKey: 'sidenav.appearance.dark' as TranslationKey,
    icon: <MoonIcon />,
  },
  {
    value: 'system' as AppearanceMode,
    tooltipKey: 'sidenav.appearance.system' as TranslationKey,
    icon: <MonitorIcon />,
  },
];

/**
 * Segmented control for appearance mode, rendered inside the
 * UserAccountMenu header area (between user info and menu items).
 */
export function AppearanceModeMenuItems() {
  const { mode, setMode } = useAppearanceMode();
  const { t } = useTranslations();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();

  return (
    <div className="px-2 pb-1.5 pt-1">
      <SegmentedControl
        value={mode}
        onValueChange={(value) => {
          if (value === 'light' || value === 'dark' || value === 'system') {
            if (isOfflineGated) {
              setMode(value, { suppressNetworkSideEffects: true });
            } else {
              setMode(value);
            }
          }
        }}
      >
        {/* Pin to 32/24 -- DS default of 36/28 is too tall for this dense menu surface. */}
        <SegmentedControlList className="!h-8 w-full">
          {APPEARANCE_OPTIONS.map(({ value, tooltipKey, icon }) => (
            <Tooltip key={value} delayDuration={300}>
              <TooltipTrigger asChild>
                <span className="flex flex-1">
                  <SegmentedControlTrigger
                    value={value}
                    iconOnly
                    aria-label={t(tooltipKey)}
                    className="!h-6 w-full"
                    data-testid={`appearance-mode-${value}`}
                  >
                    {icon}
                  </SegmentedControlTrigger>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t(tooltipKey)}</TooltipContent>
            </Tooltip>
          ))}
        </SegmentedControlList>
      </SegmentedControl>
    </div>
  );
}
